import type { StoreActionApi } from 'react-sweet-state';
import type { GraphQlData } from '@atlassian/jira-issue-gira-transformer-types';
import type { ChildIssueFieldKeys } from '@atlassian/jira-issue-shared-types';
import type { mainIssueAggQuery$data as mainIssueAggQueryResponse } from '@atlassian/jira-relay/src/__generated__/mainIssueAggQuery.graphql';
import type { IssueKey, IssueId } from '@atlassian/jira-shared-types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type HookProps = Record<any, any>;

export type RapidViewMap = IssueKey[][];

export const VIEW_MODE_MODAL = 'MODAL';
export const VIEW_MODE_SIDEBAR = 'SIDEBAR';
export const VIEW_MODE_DEFAULT = 'DEFAULT';

export type ViewModeType =
	| typeof VIEW_MODE_MODAL
	| typeof VIEW_MODE_SIDEBAR
	| typeof VIEW_MODE_DEFAULT;

export const FIELD_ITEM = 'FIELD' as const;
export const DEV_SUMMARY_ITEM = 'DEV_SUMMARY' as const;
export const RELEASES_PANEL_ITEM = 'RELEASES_PANEL' as const;
export const PANEL_ITEM = 'PANEL' as const;
export const TAB_ITEM = 'TAB' as const;

export type LayoutItemType =
	| typeof FIELD_ITEM
	| typeof DEV_SUMMARY_ITEM
	| typeof RELEASES_PANEL_ITEM
	| typeof PANEL_ITEM
	| typeof TAB_ITEM;

export type LayoutItem = {
	id: string;
	type: LayoutItemType;
};

export type OnIssueKeyChangeFn = (arg1: {
	fromIssueKey: IssueKey;
	toIssueKey: IssueKey;
	meta: {
		location: string;
	};
}) => void;

export type IssueNavigatorData = {
	issueKey: string;
	issueKeys: (string | undefined)[];
	knownIssueKeys: string[];
	issuesCount: number;
};

export type IssueContext = {
	analyticsSource: string | undefined;
	backButtonUrl: string | undefined;
	issueNavigatorData?: IssueNavigatorData;
	boardId: number | undefined;
	boardType: string | undefined;
	childIssuesLimit?: number | null | undefined;
	childIssuesIssueLimitUrls?: Record<ChildIssueFieldKeys, string | undefined>;
	estimationStatistic: string | undefined;
	isLoadedWithPage: boolean;
	isLoading: boolean;
	isModal: boolean;
	isSpaEnabled: boolean;
	issueKey: IssueKey;
	isArchived?: boolean;
	isClassifyEditEnabled?: boolean;
	issueId?: IssueId;
	loginRedirectUrl: string | undefined;
	maxTokenLength: number;
	rapidViewId: number | undefined;
	rapidViewMap: RapidViewMap | undefined;
	viewMode: ViewModeType | undefined;
	onIssueKeyChange: OnIssueKeyChangeFn | undefined;
	viewScreenId?: number | undefined;
	isAiOptInEnabled?: boolean;
	// --BEGIN-- remove when cleaning up: issue.details.gira-agg-consistency-check-master-flag
	aggResponse?: mainIssueAggQueryResponse;
	legacyGiraResponse?: GraphQlData;
	// --END-- remove when cleaning up: issue.details.gira-agg-consistency-check-master-flag
};

export type State = IssueContext;

export type StoreApi = StoreActionApi<State>;

export type IssueContextServiceActions = {
	setIssueContext: (issueContext: IssueContext) => void;
	mergeIssueContext: (partialIssueContext: Partial<IssueContext>) => void;
	getChildIssuesLimit: () => number;
	getMaxTokenLength: () => number;
	getIsLoadedWithPage: () => boolean;
	getBackButtonUrl: () => string | undefined;
	getIsSpaEnabled: () => boolean;
	getIssueKey: () => IssueKey;
	// --NEXTLINE-- remove conditional when cleaning up: gic.positioning
	setDetailViewAsModal?: () => void;
	setDetailViewAsSidebar?: (willSave?: boolean) => void;
	resetDetailView?: (willSave?: boolean) => void;
};
